import React, { useState, useCallback } from 'react';
import Map, { Marker, Popup, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPin } from 'lucide-react';
import { DummyProduct } from '../../lib/dummyProducts';

interface ProductMapProps {
  products: DummyProduct[];
  height?: string;
  className?: string;
  onProductClick?: (product: DummyProduct) => void;
}

export const ProductMap: React.FC<ProductMapProps> = ({ 
  products, 
  height = '600px', 
  className = '', 
  onProductClick 
}) => {
  const [popupInfo, setPopupInfo] = useState<DummyProduct | null>(null);

  // Filter products that have location data
  const productsWithLocation = products.filter(product => product.lat && product.lng);

  // Calculate center point of all locations, default to Nepal center
  const centerLat = productsWithLocation.length > 0 
    ? productsWithLocation.reduce((sum, product) => sum + product.lat, 0) / productsWithLocation.length 
    : 28.3949; // Center of Nepal
  const centerLng = productsWithLocation.length > 0 
    ? productsWithLocation.reduce((sum, product) => sum + product.lng, 0) / productsWithLocation.length 
    : 84.1240; // Center of Nepal

  // Calculate appropriate zoom level
  const getZoomLevel = () => {
    if (productsWithLocation.length === 0) return 7;
    if (productsWithLocation.length === 1) return 10;
    if (productsWithLocation.length <= 3) return 8;
    return 7; // Show more of Nepal for multiple products
  };

  const onMarkerClick = useCallback((product: DummyProduct) => {
    setPopupInfo(product);
    if (onProductClick) {
      onProductClick(product);
    }
  }, [onProductClick]);

  const onPopupClose = useCallback(() => {
    setPopupInfo(null);
  }, []);

  if (productsWithLocation.length === 0) {
    return (
      <div 
        className={`flex items-center justify-center bg-gradient-to-br from-[#E8F5E9] to-[#E8F5E9]/20 rounded-2xl ${className}`}
        style={{ height }}
      >
        <div className="text-center text-gray-500">
          <MapPin className="w-12 h-12 mx-auto mb-3" style={{ color: 'rgba(46, 125, 50, 0.3)' }} />
          <p className="text-lg font-medium">No location data available</p>
          <p className="text-sm mt-1">Products will appear here when location data is added</p>
        </div>
      </div>
    );
  }

  // MapTiler Streets style - matches home page
  const mapTilerStyle = `https://api.maptiler.com/maps/streets/style.json?key=zSKe702Zq1told0U6bDZ`;

  return (
    <div className={`relative rounded-2xl overflow-hidden border-4 border-[#2E7D32]/20 shadow-2xl ${className}`} style={{ height }}>
      <Map
        initialViewState={{
          longitude: centerLng,
          latitude: centerLat,
          zoom: getZoomLevel(),
          pitch: 0,
          bearing: 0
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapTilerStyle}
        attributionControl={false}
        reuseMaps={true}
      >
        {/* Navigation Controls - Top Right */}
        <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
          <NavigationControl 
            showCompass={true}
            showZoom={true}
            visualizePitch={true}
          />
        </div>

        {/* Green Circular Markers - matches home page style */}
        {productsWithLocation.map((product) => (
          <Marker
            key={product.id}
            longitude={product.lng}
            latitude={product.lat}
            anchor="center"
            onClick={(e) => {
              e.originalEvent.stopPropagation();
              onMarkerClick(product);
            }}
          >
            <div
              className="cursor-pointer group relative"
              style={{
                width: '32px',
                height: '32px',
                backgroundColor: '#2E7D32',
                border: '3px solid white',
                borderRadius: '50%',
                boxShadow: '0 2px 8px rgba(46, 125, 50, 0.4)',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.2)';
                e.currentTarget.style.backgroundColor = '#4CAF50';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.backgroundColor = '#2E7D32';
              }}
            />
          </Marker>
        ))}

        {/* Product Popup - simplified like home page */}
        {popupInfo && (
          <Popup
            longitude={popupInfo.lng}
            latitude={popupInfo.lat}
            anchor="bottom"
            onClose={onPopupClose}
            closeButton={false}
            closeOnClick={false}
            className="farmero-popup"
            maxWidth="300px"
          >
            <div style={{ padding: '8px' }}>
              <div style={{ fontWeight: 600, color: '#2E7D32', marginBottom: '4px' }}>
                {popupInfo.product_name || popupInfo.name}
              </div>
              <div style={{ fontSize: '12px', color: '#717171', marginBottom: '4px' }}>
                {popupInfo.location?.farm_name || popupInfo.origin || 'Nepal'}
              </div>
              <div style={{ fontWeight: 700, color: '#4CAF50' }}>
                Rs {popupInfo.price?.toFixed(2) || '0.00'}
              </div>
            </div>
          </Popup>
        )}
      </Map>
    </div>
  );
};

